import {
  all,
  call,
  delay,
  fork,
  put,
  select,
  takeEvery,
  takeLatest,
} from '@redux-saga/core/effects';
import { createAction, createReducer, createSelector } from '@reduxjs/toolkit';
import { push } from 'connected-react-router';
import { CURRENT_HEARINGS_URL, HOME_URL } from '../constants/urls';
import { AuthenticationConnector } from './connectors/authenticationConnector';
import { getAttr, getEntityId } from './helpers/apiHelpers';
import { awaitApiCall, generateGetAllApiCall } from './helpers/sagaHelpers';
import { getRequestData } from './helpers/selectorHelpers';
import { setGlobalMessage, globalMessageModes } from './globalMessageSlice';

// -------- Actions --------
const isAuthorizedInitialize = createAction('authentication/isAuthorized/init');
const isAuthorizedSuccess = createAction('authentication/isAuthorized/success');
const isAuthorizedError = createAction('authentication/isAuthorized/error');

const getCurrentUserInitialize = createAction(
  'authentication/getCurrentUser/init',
);
const getCurrentUserSuccess = createAction(
  'authentication/getCurrentUser/success',
);

const setIsAuthorizing = createAction('authentication/setIsAuthorizing');
const loginFlowInitialize = createAction('authentication/loginFlow/initialize');
const loginFlowFinalized = createAction('authentication/loginFlow/finalized');

const doLoginInitialize = createAction('authentication/doLogin/init');
const doLogoutInitialize = createAction('authentication/doLogout/init');

const getRefreshToken = createAction('authentication/getRefreshToken');
const doGetRefreshToken = createAction('authentication/doGetRefreshToken/init');
const waitToRenewAccessToken = createAction(
  'authentication/waitToRefreshAccessToken',
);
const renewAccessToken = createAction('authentication/renewAccessToken/init');

const initialState = {
  isAuthorized: false,
  isAuthorizing: false,
  currentUser: null,
};

// --------- Reducers ----------
const authenticationReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(isAuthorizedInitialize, (state) => {
      state.isAuthorized = false;
    })
    .addCase(isAuthorizedSuccess, (state, action) => {
      const isAuthorized = action.payload.requestData;
      state.isAuthorized = isAuthorized;
    })
    .addCase(isAuthorizedError, (state) => {
      state.isAuthorized = false;
    })
    .addCase(setIsAuthorizing, (state, action) => {
      state.isAuthorizing = action.payload;
    })
    .addCase(getCurrentUserSuccess, (state, action) => {
      const user = getRequestData(action);
      state.currentUser = user;
    });
});

// -------- Sagas ----------
export function* authenticationSaga() {
  yield all([
    fork(loginFlowSaga),
    fork(doLoginSaga),
    fork(isAuthorizedSaga),
    fork(getCurrentUserSaga),
    fork(doLogoutSaga),
    fork(doGetRefreshTokenSaga),
    fork(getRefreshTokenSaga),
    fork(waitToRenewAccessTokenSaga),
    fork(renewAccessTokenSaga),
  ]);
}

function* getCurrentUserSaga() {
  yield takeEvery(
    getCurrentUserInitialize.type,
    generateGetAllApiCall(AuthenticationConnector, function* (connector, _) {
      return yield call([connector, connector.me]);
    }),
  );
}

function* isAuthorizedSaga() {
  yield takeEvery(
    isAuthorizedInitialize.type,
    generateGetAllApiCall(AuthenticationConnector, function* (connector, _) {
      return yield call([connector, connector.authorized]);
    }),
  );
}

function* doLoginSaga() {
  yield takeEvery(
    doLoginInitialize.type,
    generateGetAllApiCall(AuthenticationConnector, function* (connector, _) {
      return yield call(
        [connector, connector.login],
        window.location.origin + CURRENT_HEARINGS_URL,
      );
    }),
  );
}

function* doLogoutSaga() {
  yield takeEvery(
    doLogoutInitialize.type,
    generateGetAllApiCall(AuthenticationConnector, function* (connector, _) {
      return yield call(
        [connector, connector.logout],
        window.location.origin + HOME_URL,
      );
    }),
  );
}

function* loginFlowSaga() {
  yield takeEvery(loginFlowInitialize.type, function* () {
    yield put(setIsAuthorizing(true));

    const isAuthorizedInitAction = yield put(isAuthorizedInitialize());
    yield awaitApiCall(isAuthorizedInitAction);

    const isAuthorized = yield select(isAuthorizedSelector);
    let couldGetCurrentUser = false;

    if (isAuthorized) {
      const getCurrentUserInitAction = yield put(getCurrentUserInitialize());
      const getCurrentUserResult = yield awaitApiCall(getCurrentUserInitAction);

      if (getCurrentUserResult.success) {
        couldGetCurrentUser = true;
        yield put(getRefreshToken());
      }
    }
    if (!(isAuthorized && couldGetCurrentUser)) {
      yield put(push(HOME_URL));
    }

    yield put(setIsAuthorizing(false));
    yield put(loginFlowFinalized());
  });
}

function* getRefreshTokenSaga() {
  yield takeEvery(getRefreshToken.type, function* () {
    const doGetRefreshTokenInitAction = yield put(doGetRefreshToken());

    const doGetRefreshTokenResult = yield awaitApiCall(
      doGetRefreshTokenInitAction,
    );

    if (doGetRefreshTokenResult.success) {
      yield put(
        waitToRenewAccessToken(doGetRefreshTokenResult.success.payload),
      );
    }
  });
}

function* doGetRefreshTokenSaga() {
  yield takeEvery(
    doGetRefreshToken.type,
    generateGetAllApiCall(AuthenticationConnector, function* (connector, _) {
      return yield call([connector, connector.getRefreshToken]);
    }),
  );
}

function* waitToRenewAccessTokenSaga() {
  yield takeLatest(waitToRenewAccessToken.type, function* (action) {
    const {
      token,
      accessTokenExpirationDate,
      refreshTokenExpirationDate,
    } = action?.payload?.requestData;

    let currentDate = new Date();
    const accTokenExpDate = new Date(accessTokenExpirationDate);
    const refTokenExpDate = new Date(refreshTokenExpirationDate);
    const earliestTokenExpiration = Math.min(
      refTokenExpDate.getTime(),
      accTokenExpDate.getTime(),
    );
    const bufferTimeInMsecs = 30 * 1000;
    const retryDelayInMsecs = 8 * 1000;

    const delayUntilAccessTokenRenewalInMsecs =
      earliestTokenExpiration - currentDate.getTime() - bufferTimeInMsecs;

    // the refresh token is still valid, there is time to refresh the access token
    let didRefreshAccessToken = false;
    if (currentDate < refTokenExpDate) {
      // postpone polling for new access token, but maintain buffer for retries
      yield delay(delayUntilAccessTokenRenewalInMsecs);

      do {
        const renewAccessTokenInitAction = yield put(renewAccessToken(token));
        const renewAccessTokenResult = yield awaitApiCall(
          renewAccessTokenInitAction,
        );

        if (renewAccessTokenResult.success) {
          didRefreshAccessToken = true;
        } else {
          yield delay(retryDelayInMsecs);
        }
        currentDate = new Date();
      } while (currentDate < accTokenExpDate && !didRefreshAccessToken);
    }

    if (didRefreshAccessToken) {
      // we succeeded in refreshing the token. schedule the loop for another round once the new access token it about to expire.
      yield put(getRefreshToken());
    } else {
      // we end up here because either the refresh token is expired or we have failed to acquire a new access token. In both cases
      // this is game over for productivity in the app, so the saga dies here without reactivation.
      yield put(setGlobalMessage({ mode: globalMessageModes.loginExpired }));
    }
  });
}

function* renewAccessTokenSaga() {
  yield takeEvery(
    renewAccessToken.type,
    generateGetAllApiCall(
      AuthenticationConnector,
      function* (connector, action) {
        return yield call(
          [connector, connector.renewAccessToken],
          action.payload,
        );
      },
      true,
    ),
  );
}

// ------- Selectors --------
const selectAuthenticationState = (state) => state.app.authentication;

const isAuthorizedSelector = createSelector(
  selectAuthenticationState,
  (state) => state.isAuthorized,
);

const isAuthorizingSelector = createSelector(
  selectAuthenticationState,
  (state) => state.isAuthorizing,
);

const currentUserSelector = createSelector(
  selectAuthenticationState,
  (state) => state.currentUser,
);

const currentUserIdSelector = createSelector(currentUserSelector, (user) =>
  getEntityId(user),
);

const currentUserDisplayNameSelector = createSelector(
  currentUserSelector,
  (user) => getAttr(user, 'employeeDisplayName'),
);

const currentUserIsAdministratorSelector = createSelector(
  currentUserSelector,
  (user) => getAttr(user, 'isAdministrator'),
);

const currentUserIsHearingCreatorSelector = createSelector(
  currentUserSelector,
  (user) => getAttr(user, 'isHearingCreator'),
);

// Export selectors
export {
  isAuthorizedSelector,
  isAuthorizingSelector,
  currentUserIdSelector,
  currentUserDisplayNameSelector,
  currentUserIsAdministratorSelector,
  currentUserIsHearingCreatorSelector,
};

// Export actions
export {
  loginFlowFinalized,
  loginFlowInitialize,
  doLoginInitialize,
  doLogoutInitialize,
  getCurrentUserSuccess,
};

export default authenticationReducer;
